import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaChartPie } from "react-icons/fa6";
import { FaUsers, FaLeaf, FaIndustry, FaChartLine } from "react-icons/fa";
import {
  HiOutlinePresentationChartLine,
  HiOutlineClipboardDocumentList,
  HiOutlineCalendar,
  HiOutlineBuildingOffice,
  HiOutlineUserGroup,
  HiOutlineIdentification,
  HiOutlineGlobeAlt,
  HiOutlineUsers,
  HiOutlineCpuChip,
} from "react-icons/hi2";
import { CriteriaBreakdown } from "types/assessmentTypes";
import { getDisplayPercent } from "../helper/reportHelper";
import CircularChart from "./CircularChart";

const CRITERIA_GROUPS = {
  financial: {
    id: "financial",
    titleKey: "criteriaGroup.financial",
    color: "#35D9FF",
    criteria: [1, 2, 3, 4, 5, 6, 9],
  },
  paei: {
    id: "paei",
    titleKey: "criteriaGroup.paei",
    color: "#6CFF4A",
    criteria: [25, 28, 26, 27],
  },
  infrastructure: {
    id: "infrastructure",
    titleKey: "criteriaGroup.infrastructure",
    color: "#B45BFF",
    criteria: [19, 20, 21, 22, 23, 24],
  },
  market: {
    id: "market",
    titleKey: "criteriaGroup.market",
    color: "#1EA7FF",
    criteria: [7, 8, 10, 11, 12, 13, 14, 15, 16, 17, 18],
  },
  sustainability: {
    id: "sustainability",
    titleKey: "criteriaGroup.sustainability",
    color: "#FF7D2D",
    criteria: [29, 30, 31, 32, 33, 34, 35, 36],
  },
} as const;

type GroupKey = keyof typeof CRITERIA_GROUPS;
const GROUP_ORDER: GroupKey[] = [
  "financial",
  "paei",
  "infrastructure",
  "market",
  "sustainability",
];

const GROUP_ICONS: Record<GroupKey, any> = {
  financial: FaChartLine,
  paei: HiOutlinePresentationChartLine,
  infrastructure: FaIndustry,
  market: FaUsers,
  sustainability: FaLeaf,
};

const ICON_SETS: Record<GroupKey, any[]> = {
  financial: [
    HiOutlineClipboardDocumentList,
    HiOutlineCalendar,
    HiOutlineBuildingOffice,
    HiOutlineUserGroup,
    HiOutlineIdentification,
    HiOutlineGlobeAlt,
    HiOutlineUsers,
  ],
  paei: [
    HiOutlineUsers,
    HiOutlineUserGroup,
    HiOutlineBuildingOffice,
    HiOutlineClipboardDocumentList,
  ],
  infrastructure: [
    HiOutlineCpuChip,
    HiOutlineBuildingOffice,
    HiOutlineUsers,
    HiOutlineUserGroup,
    HiOutlineClipboardDocumentList,
    HiOutlineCalendar,
  ],
  market: [
    HiOutlineUsers,
    HiOutlineUserGroup,
    HiOutlineBuildingOffice,
    HiOutlineClipboardDocumentList,
    HiOutlineIdentification,
    HiOutlineGlobeAlt,
    HiOutlineCalendar,
    HiOutlineUsers,
    HiOutlineUserGroup,
    HiOutlineBuildingOffice,
    HiOutlineClipboardDocumentList,
  ],
  sustainability: [
    HiOutlineGlobeAlt,
    HiOutlineCalendar,
    HiOutlineUsers,
    HiOutlineBuildingOffice,
    HiOutlineClipboardDocumentList,
    HiOutlineIdentification,
    HiOutlineUserGroup,
    HiOutlineUsers,
  ],
};

function getColorPalette(groupKey: GroupKey, index: number, total: number) {
  const base = CRITERIA_GROUPS[groupKey]?.color || "#FFFFFF";
  const hex = base.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const variation = 0.03;
  const factor = 1 + (index - (total - 1) / 2) * variation;
  return `rgb(${Math.round(r * factor)}, ${Math.round(
    g * factor
  )}, ${Math.round(b * factor)})`;
}

function getCriteriaIcon(groupKey: GroupKey, index: number) {
  const icons = ICON_SETS[groupKey] || [HiOutlineClipboardDocumentList];
  return icons[index % icons.length];
}

// اندازه‌های دقیق — همه چیز بر اساس ارتفاع در دسترس
const PAGE_CONTENT_HEIGHT = 800;
const BLOCK_HEIGHT = PAGE_CONTENT_HEIGHT / 2;
const CHART_SIZE = 330;
const LEGEND_HEIGHT = 44;
const GAP_BETWEEN = 8;

interface PerformanceChartsPdfProps {
  assessment: any;
  items: Array<GroupKey | "overview">;
  fontFamily?: string;
}

export default function PerformanceChartsPdf({
  assessment,
  items,
}: PerformanceChartsPdfProps) {
  const { t } = useTranslation();

  const groupScores = useMemo(() => {
    if (!assessment?.scoringResults?.[0]?.criteriaBreakdown) return null;
    const breakdown: CriteriaBreakdown[] =
      assessment.scoringResults[0].criteriaBreakdown;

    const groups = GROUP_ORDER.map((key) => {
      const cfg = CRITERIA_GROUPS[key];
      const items = cfg.criteria
        .map((idx) => {
          const found = breakdown.find((c) => c.criteriaIndex === idx);
          if (!found) return null;
          const score = getDisplayPercent(found);
          if (score == null || isNaN(score)) return null;
          return {
            criteriaIndex: idx,
            label: t(`criteria.${idx}`),
            score: Math.round(score * 100) / 100,
          };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null);

      const avg =
        items.length > 0
          ? Math.round(
              (items.reduce((s, x) => s + x.score, 0) / items.length) * 100
            ) / 100
          : 0;

      return {
        key,
        title: t(cfg.titleKey),
        color: cfg.color,
        icon: GROUP_ICONS[key],
        items,
        avg,
        hasData: items.length > 0,
      };
    });

    const existing = groups.filter((g) => g.hasData);
    const totalAvg =
      existing.length > 0
        ? Math.round(
            (existing.reduce((s, g) => s + g.avg, 0) / existing.length) * 100
          ) / 100
        : 0;

    return { groups, totalAvg };
  }, [assessment, t]);

  if (!groupScores) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: PAGE_CONTENT_HEIGHT,
        gap: GAP_BETWEEN,
      }}
    >
      {items.map((itemKey, idx) => (
        <div
          key={`${itemKey}-${idx}`}
          style={{
            width: "100%",
            height: BLOCK_HEIGHT - GAP_BETWEEN / 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            overflow: "hidden",
          }}
        >
          {itemKey === "overview" ? (
            <OverviewBlock groupScores={groupScores} t={t} />
          ) : (
            <GroupBlock
              groupKey={itemKey as GroupKey}
              groupScores={groupScores}
              t={t}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// Overview
function OverviewBlock({ groupScores, t }: { groupScores: any; t: any }) {
  const data = groupScores.groups
    .filter((g: any) => g.hasData)
    .map((g: any) => ({
      id: g.key,
      title: g.title,
      value: g.avg,
      color: g.color,
      icon: g.icon,
    }));

  const goldColor = "#FFD700";

  return (
    <>
      {/* ============ Legend ============ */}
      <div
        style={{
          height: LEGEND_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          padding: "0 10px",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0,
            borderRadius: 999,
            background: "rgba(8,17,38,0.85)",
            border: `1.5px solid ${goldColor}AA`,
            boxShadow: `0 0 20px ${goldColor}33, inset 0 0 12px ${goldColor}10`,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: 6,
              alignSelf: "stretch",
              background: `linear-gradient(180deg, ${goldColor}, #B8860B)`,
              boxShadow: `0 0 12px ${goldColor}`,
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "5px 8px 5px 10px",
            }}
          >
            <FaChartPie size={14} color={goldColor} />
          </div>

          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#FFFFFF",
              padding: "5px 10px 5px 0",
            }}
          >
            {t("assessment.report.circularChart")}
          </span>

          <div
            style={{
              width: 1,
              height: 20,
              background: `${goldColor}44`,
            }}
          />

          <span
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: goldColor,
              padding: "5px 14px 5px 12px",
              textShadow: `0 0 8px ${goldColor}66`,
            }}
          >
            {groupScores.totalAvg.toFixed(1)}%
          </span>
        </div>
      </div>

      {/* ============ Chart ============ */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <div
          style={{
            width: CHART_SIZE,
            height: CHART_SIZE,
            position: "relative",
          }}
        >
          <CircularChart
            data={data}
            centerTitle={groupScores.totalAvg.toFixed(1)}
            centerSubtitle={t("assessment.report.average")}
            centerIcon={FaChartPie}
            centerBorderColor={goldColor}
            showPercentage
            showValue={false}
            totalValue={100}
            size={CHART_SIZE}
            pdfMode
          />
        </div>
      </div>
    </>
  );
}

function GroupBlock({
  groupKey,
  groupScores,
  t,
}: {
  groupKey: GroupKey;
  groupScores: any;
  t: any;
}) {
  const group = groupScores.groups.find((g: any) => g.key === groupKey);
  if (!group || !group.hasData) return null;

  const data = group.items.map((it: any, i: number) => ({
    id: it.criteriaIndex,
    title: it.label,
    fullTitle: it.label,
    value: it.score,
    color: getColorPalette(groupKey, i, group.items.length),
    icon: getCriteriaIcon(groupKey, i),
  }));

  return (
    <>
      {/* ============ Legend ============ */}
      <div
        style={{
          height: LEGEND_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          padding: "0 10px",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0,
            borderRadius: 999,
            background: "rgba(8,17,38,0.85)",
            border: `1.5px solid ${group.color}AA`,
            boxShadow: `0 0 20px ${group.color}33, inset 0 0 12px ${group.color}10`,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: 6,
              alignSelf: "stretch",
              background: group.color,
              boxShadow: `0 0 12px ${group.color}`,
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "5px 8px 5px 10px",
            }}
          >
            <group.icon size={14} color={group.color} />
          </div>

          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#FFFFFF",
              padding: "5px 10px 5px 0",
            }}
          >
            {group.title}
          </span>

          <div
            style={{
              width: 1,
              height: 20,
              background: `${group.color}44`,
            }}
          />

          <span
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: group.color,
              padding: "5px 14px 5px 12px",
              textShadow: `0 0 8px ${group.color}66`,
            }}
          >
            {group.avg.toFixed(1)}%
          </span>
        </div>
      </div>

      {/* ============ Chart ============ */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: CHART_SIZE,
            height: CHART_SIZE,
            position: "relative",
          }}
        >
          <CircularChart
            data={data}
            size={CHART_SIZE}
            pdfMode
            centerTitle={group.avg.toFixed(1)}
            centerSubtitle={t("assessment.report.average")}
            centerIcon={group.icon}
            centerBorderColor={group.color}
            showPercentage
            showValue={false}
            totalValue={100}
          />
        </div>
      </div>
    </>
  );
}
